import { useMediaQuery } from "@base/model/hooks/useMediaQuery";
import { FullPageSheet } from "@base/ui/FullPageSheet";
import { Button, Chip, Popover } from "@buildblockinc/buildblock-design-system";
import { ChevronDown, SlidersHorizontal, X } from "lucide-react";
import { type ReactNode, useState } from "react";
import {
  type DateDraft,
  type FilterDraft,
  resolveDateDraft,
  toDateDraft,
  toFilterDraft,
} from "../model/dailyLogSearchFilterDraft";
import { type FilterOption, useFilterOptionsQuery } from "../model/dailyLogSearchFilterOptions";
import {
  type DailyLogSearchConditions,
  EMPTY_SEARCH_CONDITIONS,
  hasAppliedConditions,
} from "../model/dailyLogSearchState";
import type { SearchApplyTrigger } from "../model/dailyLogSearchTelemetry";
import { useProjectToday } from "../model/useProjectToday";
import { DateFilterFields, OptionFilterFields } from "./DailyLogSearchFilterFields";

type ApplyConditions = (conditions: DailyLogSearchConditions, trigger: SearchApplyTrigger) => void;

interface DailyLogSearchFiltersProps {
  projectId: number;
  conditions: DailyLogSearchConditions;
  onApply: ApplyConditions;
}

/**
 * PC는 필터별 popover에서, 모바일은 전체 필터 panel에서 고른 뒤 Apply로 한 번에 적용한다.
 * 적용하지 않은 선택은 URL·결과에 섞이지 않는다.
 */
export function DailyLogSearchFilters({ projectId, conditions, onApply }: DailyLogSearchFiltersProps) {
  const applyFilter = (next: DailyLogSearchConditions) => onApply(next, "filter");
  const { today, retry: retryToday } = useProjectToday(projectId);
  const contractorOptions = useFilterOptionsQuery(projectId, "contractors");
  const tagOptions = useFilterOptionsQuery(projectId, "tags");

  const optionFields = {
    contractors: {
      options: contractorOptions.data,
      isLoading: contractorOptions.isLoading,
      isError: contractorOptions.isError,
      onRetry: () => contractorOptions.refetch(),
    },
    tags: {
      options: tagOptions.data,
      isLoading: tagOptions.isLoading,
      isError: tagOptions.isError,
      onRetry: () => tagOptions.refetch(),
    },
  };

  return (
    // 부모(daily-log-search-panel)가 키워드 입력과 한 줄에 배치하는 flex-wrap 행이므로,
    // 이 div 자체는 박스를 만들지 않고 자식들을 그 행에 직접 흘려보낸다 (칩 줄은 basis-full로 새 줄에 강제).
    <div className="contents">
      <div className="hidden gap-2 md:flex md:shrink-0">
        <DateFilterPopover conditions={conditions} today={today} onRetryToday={retryToday} onApply={applyFilter} />
        <OptionFilterPopover
          label="Contractors"
          selectedIds={conditions.contractorIds}
          onApply={(contractorIds) => applyFilter({ ...conditions, contractorIds })}
          {...optionFields.contractors}
        />
        <OptionFilterPopover
          label="Status"
          selectedIds={conditions.tagIds}
          onApply={(tagIds) => applyFilter({ ...conditions, tagIds })}
          {...optionFields.tags}
        />
      </div>

      <div className="shrink-0 md:hidden">
        <MobileFilterPanel
          conditions={conditions}
          today={today}
          onRetryToday={retryToday}
          onApply={applyFilter}
          optionFields={optionFields}
        />
      </div>

      <AppliedConditionChips
        conditions={conditions}
        contractorOptions={contractorOptions.data}
        tagOptions={tagOptions.data}
        onApply={onApply}
      />
    </div>
  );
}

interface FilterPopoverShellProps {
  label: string;
  /** 적용된 선택 수. 자세한 값은 아래 칩이 보여 준다 */
  activeCount: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  canApply: boolean;
  onApply: () => void;
  children: ReactNode;
}

function FilterPopoverShell({
  label,
  activeCount,
  open,
  onOpenChange,
  canApply,
  onApply,
  children,
}: FilterPopoverShellProps) {
  return (
    <Popover open={open} onOpenChange={(details) => onOpenChange(details.open)}>
      <Popover.Trigger asChild>
        <Button
          type="button"
          variant="outline"
          color={activeCount > 0 ? "info" : "secondary"}
          size="medium"
          suffixIcon={<ChevronDown width={14} height={14} aria-hidden="true" />}
          className="whitespace-nowrap"
        >
          {activeCount > 0 ? `${label} (${activeCount})` : label}
        </Button>
      </Popover.Trigger>
      {/* 닫힌 popover 내용이 접근성 트리에 남지 않도록 열렸을 때만 렌더한다.
          디자인 시스템 Popover의 기본 max-w-[224px]로는 기간 입력칸이 잘려 이 필터 창에서만 넓힌다. */}
      {open && (
        <Popover.Content className="flex flex-col gap-4 p-4 w-[360px] !max-w-[360px]">
          {children}
          <div className="flex gap-2 justify-end">
            <Button type="button" variant="outline" color="secondary" size="small" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="button" variant="fill" color="info" size="small" onClick={onApply} disabled={!canApply}>
              Apply
            </Button>
          </div>
        </Popover.Content>
      )}
    </Popover>
  );
}

interface DateFilterPopoverProps {
  conditions: DailyLogSearchConditions;
  today: string | null;
  onRetryToday: () => void;
  onApply: (conditions: DailyLogSearchConditions) => void;
}

function DateFilterPopover({ conditions, today, onRetryToday, onApply }: DateFilterPopoverProps) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<DateDraft>({ mode: "all" });
  const resolution = resolveDateDraft(draft, today);

  const handleOpenChange = (nextOpen: boolean) => {
    // 열 때마다 적용 값을 복사한다. 닫기·ESC·Cancel은 draft를 버린다.
    if (nextOpen) setDraft(toDateDraft(conditions, today));
    setOpen(nextOpen);
  };

  const handleApply = () => {
    if (!resolution.ok) return;
    onApply({ ...conditions, startDate: resolution.startDate, endDate: resolution.endDate });
    setOpen(false);
  };

  return (
    <FilterPopoverShell
      label="Date"
      activeCount={conditions.startDate || conditions.endDate ? 1 : 0}
      open={open}
      onOpenChange={handleOpenChange}
      canApply={resolution.ok}
      onApply={handleApply}
    >
      <DateFilterFields draft={draft} onChange={setDraft} today={today} onRetryToday={onRetryToday} />
    </FilterPopoverShell>
  );
}

interface OptionFieldState {
  options: FilterOption[] | undefined;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
}

interface OptionFilterPopoverProps extends OptionFieldState {
  label: "Contractors" | "Status";
  selectedIds: number[];
  onApply: (ids: number[]) => void;
}

function OptionFilterPopover({ label, selectedIds, onApply, ...fieldState }: OptionFilterPopoverProps) {
  const [open, setOpen] = useState(false);
  const [draftIds, setDraftIds] = useState<number[]>([]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) setDraftIds(selectedIds);
    setOpen(nextOpen);
  };

  const handleApply = () => {
    onApply(draftIds);
    setOpen(false);
  };

  return (
    <FilterPopoverShell
      label={label}
      activeCount={selectedIds.length}
      open={open}
      onOpenChange={handleOpenChange}
      canApply
      onApply={handleApply}
    >
      <OptionFilterFields label={label} selectedIds={draftIds} onChange={setDraftIds} {...fieldState} />
    </FilterPopoverShell>
  );
}

interface MobileFilterPanelProps {
  conditions: DailyLogSearchConditions;
  today: string | null;
  onRetryToday: () => void;
  onApply: (conditions: DailyLogSearchConditions) => void;
  optionFields: { contractors: OptionFieldState; tags: OptionFieldState };
}

function MobileFilterPanel({ conditions, today, onRetryToday, onApply, optionFields }: MobileFilterPanelProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState<FilterDraft>(() => toFilterDraft(conditions, today));
  const dateResolution = resolveDateDraft(draft.date, today);
  const activeFilterCount =
    (conditions.startDate || conditions.endDate ? 1 : 0) + conditions.contractorIds.length + conditions.tagIds.length;

  const open = () => {
    setDraft(toFilterDraft(conditions, today));
    setIsOpen(true);
  };

  const close = () => setIsOpen(false);

  const handleApply = () => {
    if (!dateResolution.ok) return;
    onApply({
      ...conditions,
      startDate: dateResolution.startDate,
      endDate: dateResolution.endDate,
      contractorIds: draft.contractorIds,
      tagIds: draft.tagIds,
    });
    close();
  };

  return (
    <>
      {/* 모바일은 적용 여부와 관계없이 흰 버튼으로 두고, 적용된 조건 수는 글자로 보여 준다 */}
      <Button
        type="button"
        variant="outline"
        color="secondary"
        size="medium"
        className="bg-white"
        onClick={open}
        prefixIcon={<SlidersHorizontal width={14} height={14} aria-hidden="true" />}
      >
        {activeFilterCount > 0 ? `Filters (${activeFilterCount})` : "Filters"}
      </Button>

      <FullPageSheet isOpen={isOpen} onClose={close} ariaLabel="Filters">
        {isOpen && (
          <div className="flex flex-col min-h-full">
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h2 className="text-title6 text-gray-1000">Filters</h2>
              <button type="button" onClick={close} aria-label="Close filters">
                <X width={20} height={20} />
              </button>
            </div>

            <div className="flex flex-col flex-1 gap-4 p-4">
              <DateFilterFields
                draft={draft.date}
                onChange={(date) => setDraft((prev) => ({ ...prev, date }))}
                today={today}
                onRetryToday={onRetryToday}
              />
              <hr className="border-gray-200" />
              <OptionFilterFields
                label="Contractors"
                selectedIds={draft.contractorIds}
                onChange={(contractorIds) => setDraft((prev) => ({ ...prev, contractorIds }))}
                {...optionFields.contractors}
              />
              <hr className="border-gray-200" />
              <OptionFilterFields
                label="Status"
                selectedIds={draft.tagIds}
                onChange={(tagIds) => setDraft((prev) => ({ ...prev, tagIds }))}
                {...optionFields.tags}
              />
            </div>

            <div className="flex sticky bottom-0 gap-2 p-4 bg-white border-t border-gray-200 pb-[calc(env(safe-area-inset-bottom)+16px)]">
              <Button type="button" variant="outline" color="secondary" className="flex-1" onClick={close}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="fill"
                color="info"
                className="flex-1"
                onClick={handleApply}
                disabled={!dateResolution.ok}
              >
                Apply
              </Button>
            </div>
          </div>
        )}
      </FullPageSheet>
    </>
  );
}

interface AppliedConditionChipsProps {
  conditions: DailyLogSearchConditions;
  contractorOptions: FilterOption[] | undefined;
  tagOptions: FilterOption[] | undefined;
  onApply: ApplyConditions;
}

/** 적용된 조건별 해제. 키워드 X는 키워드만, 날짜 X는 날짜만, Reset all은 전체를 지운다 */
function AppliedConditionChips({ conditions, contractorOptions, tagOptions, onApply }: AppliedConditionChipsProps) {
  if (!hasAppliedConditions(conditions)) return null;

  const remove = (next: DailyLogSearchConditions) => onApply(next, "remove");

  const dateLabel = formatDateRange(conditions.startDate, conditions.endDate);

  return (
    <ul className="flex flex-wrap gap-2 items-center basis-full" aria-label="Applied filters">
      {conditions.keyword && (
        <ConditionChip
          label={`"${conditions.keyword}"`}
          removeLabel="Remove keyword"
          onRemove={() => remove({ ...conditions, keyword: "" })}
        />
      )}
      {dateLabel && (
        <ConditionChip
          label={dateLabel}
          removeLabel="Remove date range"
          onRemove={() => remove({ ...conditions, startDate: null, endDate: null })}
        />
      )}
      {conditions.contractorIds.map((id) => (
        <OptionConditionChip
          key={`contractor-${id}`}
          kind="contractor"
          id={id}
          options={contractorOptions}
          onRemove={() =>
            remove({ ...conditions, contractorIds: conditions.contractorIds.filter((selected) => selected !== id) })
          }
        />
      ))}
      {conditions.tagIds.map((id) => (
        <OptionConditionChip
          key={`tag-${id}`}
          kind="status"
          id={id}
          options={tagOptions}
          onRemove={() => remove({ ...conditions, tagIds: conditions.tagIds.filter((selected) => selected !== id) })}
        />
      ))}
      <li>
        <Button
          type="button"
          variant="ghost"
          color="info"
          size="small"
          onClick={() => onApply(EMPTY_SEARCH_CONDITIONS, "reset")}
        >
          Reset all
        </Button>
      </li>
    </ul>
  );
}

interface OptionConditionChipProps {
  kind: "contractor" | "status";
  id: number;
  options: FilterOption[] | undefined;
  onRemove: () => void;
}

/** 선택지 검색 결과와 무관하게 선택된 ID를 유지한다. 삭제·타 프로젝트 ID는 오류로 표시한다 */
function OptionConditionChip({ kind, id, options, onRemove }: OptionConditionChipProps) {
  const name = options?.find((option) => option.id === id)?.name;
  const isUnavailable = options !== undefined && name === undefined;
  const label = getOptionChipLabel(kind, id, name, isUnavailable);

  return <ConditionChip label={label} removeLabel={`Remove ${label}`} onRemove={onRemove} isInvalid={isUnavailable} />;
}

function getOptionChipLabel(
  kind: "contractor" | "status",
  id: number,
  name: string | undefined,
  isUnavailable: boolean,
) {
  if (name !== undefined) return name;
  if (isUnavailable) return `Unavailable ${kind}`;
  return `${kind === "contractor" ? "Contractor" : "Status"} #${id}`;
}

interface ConditionChipProps {
  label: string;
  removeLabel: string;
  onRemove: () => void;
  isInvalid?: boolean;
}

const DESKTOP_MEDIA_QUERY = "(min-width: 768px)";

function ConditionChip({ label, removeLabel, onRemove, isInvalid = false }: ConditionChipProps) {
  const isDesktop = useMediaQuery(DESKTOP_MEDIA_QUERY);
  const variant = isDesktop ? "fill" : "outline";
  const color = isInvalid ? "error" : isDesktop ? "info" : "default";

  return (
    <li className="max-w-full">
      <Chip
        size="md"
        variant={variant}
        radius="round"
        color={color}
        suffixIcon={
          <button type="button" onClick={onRemove} aria-label={removeLabel} className="flex shrink-0">
            <X width={14} height={14} />
          </button>
        }
      >
        <span className="truncate" title={label}>
          {label}
        </span>
      </Chip>
    </li>
  );
}

function formatDateRange(startDate: string | null, endDate: string | null): string | null {
  if (startDate && endDate) return startDate === endDate ? startDate : `${startDate} – ${endDate}`;
  if (startDate) return `From ${startDate}`;
  if (endDate) return `Until ${endDate}`;
  return null;
}
